import { useMutation, useQueryClient } from '@tanstack/react-query';
import useGlynt from './useGlynt';
import { clearCachedCertification } from '../../utils/certificationCache';
import { IRQCallbacks } from '../../types/my-types';
import { IGroundTruthCreateParams } from '../../api/types';

type IGroundTruthCreateMutationParams = {
  trainingSetId: string;
  mutationData: IGroundTruthCreateParams;
};

export const useGroundTruthCreate = (optimisticUpdate: boolean, callbacks?: IRQCallbacks) => {
  const glynt = useGlynt();
  const cache = useQueryClient();

  return useMutation({
    mutationFn: async (data: IGroundTruthCreateMutationParams) => {
      return await (await glynt).groundTruths.create(data.mutationData);
    },

    ...callbacks,

    onSettled: async (data, error, variables) => {
      // Only on success: a failed edit revoked nothing server-side.
      if (!error) {
        await clearCachedCertification(cache, variables.trainingSetId);
      }

      // Invalidate mark validation for this document
      cache.invalidateQueries({
        queryKey: [
          'mark-validation',
          variables.trainingSetId,
          variables.mutationData.document,
        ]
      });

      if (!optimisticUpdate) {
        cache.invalidateQueries({
          queryKey: ['groundTruths']
        });
      }

      // Call the original onSettled if provided
      callbacks?.onSettled?.();
    },

    onMutate: async (data) => {
      if (optimisticUpdate) {
        // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
        await cache.cancelQueries({
          queryKey: [
            'groundTruths',
            data.mutationData.document,
            data.trainingSetId,
          ]
        });

        // // Snapshot the previous value
        const previousGroundTruths: any = cache.getQueryData([
          'groundTruths',
          data.mutationData.document,
          data.trainingSetId,
        ]);

        // Create new ground truth
        const newGroundTruth = {
          content: data.mutationData.content,
          document: 'TEMP_DATA',
          field: 'TEMP_DATA',
          id: 'TEMP_DATA',
          token_data: data.mutationData.token_data,
          updated_at: 'TEMP_DATA',
          url: 'TEMP_DATA',
        };

        // Create new object with new ground truth in the results object
        const updatedGroundTruths = previousGroundTruths;
        updatedGroundTruths['pages'][0].results.push(newGroundTruth);

        // Update the corrected value in the cache
        cache.setQueryData(
          ['groundTruths', data.mutationData.document, data.trainingSetId],
          updatedGroundTruths
        );

        // Return a context object with the snapshotted value
        return { previousGroundTruths };
      }
    }
  });
};
